import type { CatalogNote } from './catalog';

/**
 * The ONE law for what the web hides by default — this client's answer to
 * "every feature re-learns which items are visible". Every surface that lists
 * notes asks this function; none re-derives the rule.
 *
 * Returns the reason a note is held back (`deleted`, `done`, `archived`,
 * `snoozed` …), or null when it shows. The word doubles as the badge a
 * reveal mode can print.
 *
 * The rules mirror the app's feeds:
 * - a tombstone row is a deleted or archived unit — never current material;
 * - the lifecycle words hide (`done`, `archived`, `moved`, `hidden`,
 *   `bounce`), `hidden-until <ms>` only while the instant is ahead;
 * - `dimmed` shows — a preference about prominence, not presence;
 * - a word this build does not know shows: new states must opt into hiding,
 *   never fall into it;
 * - a note the catalog does not know shows — the honest default for anything
 *   catalogued before the visibility word existed.
 */
export function hiddenStateOf(state: CatalogNote | undefined, nowMs: number): string | null {
  if (!state) return null;
  if (state.kind === 'tombstone') return 'deleted';
  const v = state.v;
  if (!v) return null;
  if (v === 'done' || v === 'archived' || v === 'moved' || v === 'hidden' || v === 'bounce') return v;
  if (v.startsWith('hidden-until')) {
    const until = Number(v.slice('hidden-until'.length).trim());
    return Number.isFinite(until) && until > nowMs ? 'snoozed' : null;
  }
  return null;
}
